import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createReplyWithMaterials } from '@/services/api';
import type { CreateReplyWithMaterialsRequest, ReplyWithAttachmentsResponse } from '@/types';

interface UseReplyWithMaterialsOptions {
  ticketId: string;
  onSuccess?: (data: ReplyWithAttachmentsResponse) => void;
  onError?: (error: Error) => void;
}

/**
 * React Query mutation hook for creating a ticket reply with marketing material attachments
 * Includes automatic query invalidation on success and idempotency key generation
 */
export function useReplyWithMaterials({ ticketId, onSuccess, onError }: UseReplyWithMaterialsOptions) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: CreateReplyWithMaterialsRequest) => {
      const idempotencyKey = crypto.randomUUID();
      return createReplyWithMaterials(ticketId, request, idempotencyKey);
    },
    onSuccess: (data) => {
      // Refresh the conversation thread and single-ticket view
      queryClient.invalidateQueries({ queryKey: ['ticket-replies', ticketId] });
      queryClient.invalidateQueries({ queryKey: ['ticket', ticketId] });
      // Invalidate the list so CustomerResponded + UpdatedAt refresh in the grid
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      
      onSuccess?.(data);
    },
    onError: (error: Error) => {
      onError?.(error);
    },
  });
}
